<template>
  <div class="landing-page" :class="[data.banner ? 'banner' : '', $route.name, data.className]">
    <client-only>
      <HeroSection
        :options="data.heroSectionOptions"
        :promo-card-options="data.promoCardOptions"
        :countdown-end-date="endDate"
        :video-in-hero="data.videoInHero"
        :video-source="data.videoSource"
        @subscribe="subscribe"
      />
      <div class="site-section">
        <div v-if="getPlatformImage" class="container site image-container">
          <img loading="lazy" class="col-12" :src="getPlatformImage">
        </div>
        <ProgramStats
          v-if="data.programStatsOptions" :options="data.programStatsOptions" :button-text="`Save ${data.pagePercentOff} Now`"
          @scrollToLastSection="scrollToLastSection"
        />
        <client-only v-if="!data.videoInHero && $config.platform === 'ds'">
          <div class="video-container">
            <div class="container vv">
              <div class="col-10 offset-1">
                <h2>{{ data.videoTitle }}</h2>
                <VideoPlayer slot="player" :video-id="data.videoSource" class="video-player" />
              </div>
            </div>
          </div>
        </client-only>
        <HeroSection
          v-if="data.industryExpertsHeroSection && $config.platform === 'ds'"
          :options="data.industryExpertsHeroSection"
          left-cols="col-4"
          right-cols="col-7 offset-1"
          class="industry-experts"
        />
        <ProgramCards
          :title="data.programCardTitle" :button="data.programCardsButton" :modules-extend="data.modulesExtend"
          :no-padding-top="version === '1' && url === 'resources-centeroffer'"
          @scrollToLastSection="scrollToLastSection"
        />
        <DataScienceCards :options="data.dataScienceCardsOptions" :button-text="`Start at ${data.pagePercentOff} Off`" @scrollToLastSection="scrollToLastSection" />
        <div class="components-wrapper">
          <Reviews dark />
          <Trusted
            :title="data.trustedTitle" :footer-text="data.trustedFooterText" image-desktop="students-worldwide.png"
            image-mobile="students-worldwide-mobile.png"
          />
        </div>
        <LimitedOffer
          :options="data.limitedOfferOptions"
          :features="data.limitedOfferFeatures"
          @subscribe="subscribe"
        />
        <BottomBanner
          v-if="data.banner"
          :options="data.bottomBannerOptions"
          :countdown-end-date="endDate"
          :closed="data.bannerClosed"
          @subscribe="subscribe"
          @close="closeBanner"
        />
      </div>
    </client-only>
    <img v-if="background && $config.platform === 'ds'" class="background-image desktop" :data-src="require( `../assets/images/${background.default}`)">
    <img v-if="background && $config.platform === 'fa'" class="background-image desktop" :data-src="require(`../assets/images/fa/fa-landing/${background.default}`)">
    <img v-if="background && $config.platform === 'ds'" class="background-image mobile" :data-src="require(`../assets/images/${background.mobile}`)">
    <img v-if="background && $config.platform === 'fa'" class="background-image mobile" :data-src="require(`../assets/images/fa/fa-landing/${background.mobile}`)">
  </div>
</template>

<script>
import { merge } from 'lodash-es';
import global from '@itbpbg/lms-components/src/mixins/global';
import VideoPlayer from '@itbpbg/lms-components/src/components/VideoPlayer.vue';
import { DS_PAGES, FA_PAGES } from '../utils/landing-pages-constants.js';
import HeroSection from './Landing/HeroSection.vue';
import ProgramStats from './Landing/ProgramStats';
import ProgramCards from './Landing/ProgramCards';
import DataScienceCards from './Landing/DataScienceCards';
import Reviews from './Landing/Reviews';
import Trusted from './Landing/Trusted';
import BottomBanner from './Landing/BottomBanner';
import LimitedOffer from './Landing/LimitedOffer';

const pageDefault = {
  coupon: '',
  promoPlanType: '',
  banner: false,
  pagePercentOff: null,
  heroSectionOptions: {},
  promoCardOptions: {
    labelText: '',
    regularPrice: '',
    discountedPrice: '',
    discountedPriceCharge: '',
    billedText: '',
    countdown: false,
    buttonText: '',
    subscribeClass: '',
    promoPlanType: '',
  },
  programStatsOptions: {
    title: 'Learn Data Science from the Ground Up',
    subtitle: 'Our comprehensive training has helped people from all backgrounds and walks of life land the job they have always wanted. Step by step, no prior experience required.',
  },
  bottomBannerOptions: {
    labelText: 'Limited-Time Only',
    title: 'Save 60% with Our Exclusive Premium Plan Offer!',
    buttonText: 'Upgrade Now',
    subscribeClass: 'subscribe-annual',
    promoPlanType: 'annual',
  },
  videoTitle: 'Welcome to 365 Data Science',
  videoSource: '6282772429001',
  videoInHero: false,
  industryExpertsHeroSection: {
    title: 'Master New Skills with Industry Experts',
    titleH2: true,
    subtitle: '<p>Prepare for your best career move. Learn with world-class instructors from top companies around the world. Get personalized assistance throughout your learning process.</p>',
    companiesFooter: true,
    image: require('../assets/images/lp-instructors.png'),
  },
  programCardTitle: 'Experience a Structured Way of Learning',
  programCardsButton: false,
  dataScienceCardsOptions: {
    title: 'Accomplish Your Goals with Ease',
    subtitle: 'Your success is our priority. The 365 Data Science Program is designed to fit your busy schedule and help you achieve the results you want  faster and with less effort than any traditional graduate program.',
    firstCardTitle: 'Career Tracks',
    firstCardText: 'You set the path that best suits your professional goals. Master highly sought-after skills, pick the electives to make you stand out, ace the exams, and launch a career in a booming Data Science field.',
    secondCardTitle: 'Self-paced Online Courses',
    secondCardText: 'Access your video lessons, exercises, and real-world business cases anytime, anywhere. Set your daily learning goals and make success a routine.',
    thirdCardTitle: 'In-platform Resume Builder',
    thirdCardText: 'Our intuitive resume-building tool helps you write a sharp and effective resume in minutes and connect with potential employers from our list of partner companies.',
    dark: true,
  },
  trustedTitle: 'Trusted by over 2,000,000 students',
  trustedFooterText: 'We’ve helped students from 210 countries around the world start a career in data science.',
  limitedOfferOptions: {
    title: '',
    promoPlanType: '',
    comparisonPlanType: '',
    comparisonPlanBilledText: '',
    promoPlanBilledText: '',
    regularPrice: '',
    discountedPrice: '',
    billedComparisonPrice: '',
    discountedPriceCharge: '',
    billedComparisonCharge: '',
    promoPlanSubscribeClass: '',
    comparisonSubscribeClass: '',
    promoPlanButtonText: '',
    comparisonPlanButtonText: '',
  },
  limitedOfferFeatures: [
    {
      name: 'World-class instructors',
    },
    {
      name: 'New content',
    },
    {
      name: '150+ hours of video',
    },
    {
      name: 'Hands-on exercises',
    },
    {
      name: 'Career tracks',
    },
    {
      name: 'Downloadables',
    },
    {
      name: 'Exams',
    },
    {
      name: 'Resume builder',
    },
    {
      name: 'Certificates',
    },
    {
      name: 'Personalized support',
    },
    {
      name: 'Self-paced lessons',
    },
    {
      name: '30-day refund',
    },
  ],
};

export default {
  components: {
    VideoPlayer,
    HeroSection,
    BottomBanner,
    ProgramStats,
    ProgramCards,
    DataScienceCards,
    Reviews,
    Trusted,
    LimitedOffer,
  },
  mixins: [global],
  props: {
    url: { type: String, default: '', required: true },
    seo: { type: Object, required: true },
    userInfo: { type: Object, default: () => ({}), required: false },
    version: { type: String, default: '0', required: false },
  },
  data() {
    return {
      page: {},
      user: null,
      bannerClosed: false,
      data: pageDefault,
      endDate: new Date(),
      dataLoaded: false,
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    const brightcoveSrc = [
      { src: 'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js' },
      { src: 'https://players.brightcove.net/videojs-quality-menu/1/videojs-quality-menu.min.js' },
    ];

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps({ ...this.page }),

      script: [
        ...brightcoveSrc,
        linkedData,
      ],
    };
  },
  computed: {
    getPlatformImage() {
      if (this.$route.path === '/resources-center/offer/') {
        return '';
      }
      if (this.$store.state.layout === 'desktop' || this.$store.state.tablet) {
        if (this.$config.platform === 'ds') {
          return require('../assets/images/platform-snaps.png');
        }
        return require('../assets/images/fa/fa-landing/fa-platform-snaps.png');
      }
      if (this.$config.platform === 'ds') {
        return require('../assets/images/platform-snaps-mobile.png');
      }
      return require('../assets/images/fa/fa-landing/fa-platform-snaps-mobile.png');
    },
    background() {
      if (this.dataLoaded) {
        const bg = {
          default: 'landing-page-bg.jpg',
          mobile: 'landing-page-mobile-bg.jpg',
        };

        if (this.$config.platform === 'fa' && !this.data.background) {
          return false;
        }

        if (this.data.background) {
          if (this.data.background.default) {
            bg.default = this.data.background.default;
          }

          if (this.data.background.mobile) {
            bg.mobile = this.data.background.mobile;
          }
        }

        return bg;
      }
      return false;
    },
  },
  watch: {
    background() {
      this.$nextTick(() => {
        this.setBackground();
      });
    },
  },
  created() {
    this.page = this.seo;
    this.page.fullPath = `${this.$config.siteUrl}${this.$route.fullPath}`;
    this.page.created = this.page.created || new Date();
    this.page.updated = this.page.updated || new Date();
    this.user = this.userInfo;

    this.$nextTick(() => {
      this.getData(this.url, this.version);

      if (this.data.offerEndDate) {
        this.offerEndDate();
      }
    });
  },
  mounted() {
    if (this.data.offerEndDate) {
      this.offerEndDate();
    }

    this.setBackground();
    this.handleScroll();
  },
  methods: {
    getData(url, version) {
      let page;

      if (this.$config.platform === 'ds') {
        page = DS_PAGES[`${url}-${version}`] || DS_PAGES[url] || {};
      } else {
        page = FA_PAGES[`${url}-${version}`] || FA_PAGES[url] || {};
      }

      const { prices } = this.seo;

      if (prices) {
        page.promoCardOptions.regularPrice = `$${prices.basePrice}`;
        page.promoCardOptions.discountedPrice = `$${prices.price}`;
        page.limitedOfferOptions.regularPrice = `$${prices.basePrice}`;
        page.limitedOfferOptions.discountedPrice = `$${prices.price}`;
        page.limitedOfferOptions.billedComparisonPrice = `$${prices.basePriceComparison}`;
      }

      this.data = merge(this.data, page);
      this.dataLoaded = true;
    },
    setBackground() {
      const images = document.querySelectorAll('.background-image');

      const imageLoaded = (id) => {
        images[id].style.visibility = 'visible';
        images[id].style.opacity = 1;
      };

      images.forEach((image, id) => {
        images[id].src = images[id].getAttribute('data-src');
        image.addEventListener('load', () => imageLoaded(id));
      });
    },
    handleScroll() {
      const scroll = () => {
        if (this.$store.state.layout === 'mobile' || this.bannerClosed) {
          return;
        }

        const bottomBanner = this.$el.querySelector('.bottom-banner');
        if (bottomBanner) {
          this.toggleBanner(bottomBanner);
        }
      };
      window.addEventListener('scroll', scroll);
      this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', scroll));
    },
    toggleBanner(banner) {
      const hideFromTop = this.$el.querySelector('.landing-hero-section').getBoundingClientRect().top - 70 > 0;
      const hideFromBottom = this.$el.querySelector('.limited-offer').getBoundingClientRect().top - 400 < 0;

      if (hideFromTop || hideFromBottom) {
        banner.classList.remove('fixed');
      } else {
        banner.classList.add('fixed');
      }
    },
    closeBanner() {
      this.data.bannerClosed = true;
      const bottomBanner = this.$el.querySelector('.bottom-banner');
      bottomBanner.classList.add('closed');
    },
    scrollToLastSection() {
      const limitedOffer = document.querySelector('.limited-offer');
      const y = limitedOffer.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: y, behavior: 'smooth' });
    },
    subscribe(subscriptionPlan) {
      let promoPlanType;
      if (this.data.promoPlanType) {
        promoPlanType = this.data.promoPlanType;
      } else if (this.data.promoCardOptions && this.data.promoCardOptions.promoPlanType) {
        promoPlanType = this.data.promoCardOptions.promoPlanType;
      } else if (this.data.limitedOfferOptions && this.data.limitedOfferOptions.promoPlanType) {
        promoPlanType = this.data.limitedOfferOptions.promoPlanType;
      }

      const specialCoupon = promoPlanType === subscriptionPlan ? this.data.coupon : '';
      const plan = this.$store.getters[`subscriptions/${subscriptionPlan}`];
      this.$store.dispatch('dataLayer/addEvent', { event: 'addToCart', value: { ...plan, planTitle: plan.title, currency: plan.currency.value } });

      return this.$router.push({
        path: '/checkout/',
        query: { plan: this.$store.getters[`subscriptions/${subscriptionPlan}`].slug, coupon: specialCoupon },
      });
    },
    offerEndDate() {
      const registeredAt = new Date(this.user.registeredAt);

      this.endDate = new Date(registeredAt.setDate(registeredAt.getDate() + 5));
    },
  },
};
</script>

<style lang="scss">
.landing-page {
  padding-top: var(--spacing-80);
  background-color: var(--main-dark);
  min-height: 100vh;
  color: var(--main-light);
  position: relative;
  overflow: hidden;
  z-index: 1;

  &.banner {
    padding-bottom: var(--spacing-100);
  }

  .landing-hero-section {
    padding-top: var(--spacing-40);
    padding-bottom: 170px;

    .col-7,
    .hero-left {
      display: flex;
      flex-direction: column;
      justify-content: center;

      .heading {
        margin: 0;

        h1 {
          font-size: 44px;
        }
      }

      .subtitle > p {
        margin: 0 0 var(--spacing-16);

        &:last-of-type {
          margin: 0;
        }
      }
    }

    .card {
      background: rgba(0, 12, 31, 0.6);
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
      backdrop-filter: blur(10px);
      border-radius: 10px;

      .discounted-price,
      .subtitle {
        color: var(--main-light);
      }

      .countdown .square {
        color: var(--main-light-shade-3);
      }
    }
  }

  .site-section {
    padding: 0;

    .image-container {
      padding-bottom: var(--spacing-60);

      img {
        display: block;
        max-width: 100%;
      }
    }

    .video-container {
      .vv {
        .video-player {
          border: 14px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 4px 50px #000916;
          border-radius: 10px;
        }
      }
    }

    .industry-experts {
      .heading {
        margin-bottom: var(--spacing-16);
      }

      .subtitle p {
        font-size: 18px;
      }
    }

    .components-wrapper {
      display: flex;
      flex-direction: column;
    }

    .reviews .reviews-trustpilot .reviews-body .card-trustpilot-review .text {
      -webkit-line-clamp: unset;
    }

    .video-container,
    .industry-experts,
    .data-science-cards,
    .reviews,
    .trusted,
    .limited-offer {
      padding-top: 120px;
      padding-bottom: 120px;
    }

    .limited-offer {
      .switch-holder .switch {
        width: 45px;
        height: 20px;

        .slider {
          background: var(--accent);
        }

        .slider:before {
          width: 16px;
          height: 16px;
        }

        input:checked + .slider:before {
          transform: translateX(25px);
        }
      }

      .switch-holder {
        span {
          color: var(--main-light);

          &.unactive {
            color: var(--main-light-shade-4);
          }
        }
      }

      .plan-features .feature {
        margin-bottom: 16px;
      }
    }

    .program-stats {
      .heading {
        h2 {
          padding-right: var(--spacing-40);
        }

        .body-1 {
          margin-bottom: 0;
        }
      }

      .stats {
        display: flex;
        flex-direction: column;
        justify-content: center;

        > div span {
          line-height: 110%;
        }

        .description:last-of-type {
          padding-bottom: 0;
        }
      }
    }

    .program-cards {
      .modules {
        .module {
          max-height: 780px;

          &.expand {
            max-height: 1500px;
          }

          .module-see-more {
            bottom: 0;

            > div {
              padding: var(--spacing-12) 0 var(--spacing-60);
              background: var(--main-dark-shade-1);

              span {
                filter: drop-shadow(0 1px 10px rgba(86, 191, 199, 0.3));
              }
            }
          }
        }

        .col-6:nth-of-type(2) {
          margin-top: 150px;
        }
      }
    }

    .data-science-cards {
      .cards {
        margin-bottom: var(--spacing-28);

        &.row {
          .card {
            padding: var(--spacing-48) var(--spacing-48) 52px;
          }
        }
      }
    }

    .video-container h2 {
      text-align: center;
    }
  }

  .bottom-banner {
    background: linear-gradient(90.14deg, #293343 71.32%, #2FF 126.87%);
    height: 112px;
  }

  .background-image {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100%;
    width: 100%;
    object-fit: cover;
    z-index: -1;
    visibility: hidden;
    opacity: 0;
    transition: all var(--duration) ease;

    &.mobile {
      display: none;
    }

    &.loaded {
      visibility: visible;
      opacity: 1;
    }
  }

  &.start-learning,
  &.start-learning-m,
  &.start-learning-l {
    .landing-hero-section {
      padding-top: var(--spacing-90);
    }
  }

  &.resources-center-offer {
    .landing-hero-section {
      padding-top: var(--spacing-120);

      .card {
        .discounted-price {
          span {
            font-weight: 400;
          }
        }
      }
    }

    .site-section {
      padding-top: 80px;

      .video-container {
        padding: var(--spacing-40) 0 var(--spacing-120);

        h2 {
          margin-bottom: var(--spacing-36);
        }

        .video-player {
          border: 14px solid var(--main-dark-light-2);
        }
      }

      .program-cards {
        .heading {
          margin-bottom: 60px;

          h2 {
            margin: 0;
          }
        }

        .btn-primary {
          margin-bottom: 0;
        }
      }

      .limited-offer {
        .limited-offer-container {
          .plan-features {
            column-gap: 30px;
            width: 100%;
          }
        }
      }
    }

    .background-image.desktop {
      object-position: top;
    }
  }

  &.upgrade-offer {
    .landing-hero-section {
      .card {
        .subtitle {
          margin-bottom: var(--spacing-20);
        }

        .countdown {
          margin-bottom: var(--spacing-20);

          .square {
            padding: 0;
            justify-content: center;
          }
        }
      }
    }

    .site-section {
      .bottom-banner {
        .container-wide {
          margin: 0 auto;
          max-width: 1392px;
          width: 100%;

          .heading {
            .label-accent {
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 0;
              width: 114px;
              height: 32px;
            }

            span {
              font-weight: 500;
              font-size: 28px;
              line-height: 110%;
            }
          }

          .counter {
            padding: 0;
            margin: 0;
            max-width: 424px;
            width: 100%;
            height: 58px;

            .square {
              padding: 0;
              justify-content: center;
              filter: drop-shadow(0 3.31429px 8.28571px rgba(0, 0, 0, 0.25));
              backdrop-filter: blur(1.65714px);
              border-radius: 8px;

              &:not(:last-of-type) {
                margin-right: var(--spacing-8);
              }

              span {
                font-weight: 500;
                font-size: 32px;
                line-height: 110%;
                color: var(--main-light-shade-3);

                &.display-text {
                  font-weight: 400;
                  font-size: 14px;
                  line-height: 15px;
                }
              }
            }
          }
        }

        .icon-close {
          display: none;
        }
      }
    }
  }

  &.start-learning {
    .landing-hero-section {
      .hero-left .subtitle > p a {
        font-weight: 500;
      }
    }
  }
}

@include bp($bp-laptop) {
  .landing-page {
    .landing-hero-section {
      padding-top: var(--spacing-90);
      padding-bottom: var(--spacing-160);

      .hero-left .heading .h1 {
        font-size: 40px;
      }

      .hero-right {
        .card {
          .discounted-price {
            font-size: 60px;
            height: 60px;
          }
        }
      }
    }

    .site-section {
      .image-container {
        padding-bottom: var(--spacing-90);
      }

      .video-container {
        .vv {
          .video-player {
            box-shadow: 0 3.38144px 42.268px #000916;
            border-radius: 8px;
          }
        }
      }

      .program-cards {
        .modules {
          .col-6:nth-of-type(2) {
            margin-top: 0;
          }

          .module:not(:last-of-type) {
            margin-bottom: var(--spacing-24);
          }
        }
      }

      .data-science-cards {
        .cards.row .col-4 .card {
          padding: var(--spacing-55) var(--spacing-48);
        }
      }

      .program-stats {
        .heading h2 {
          padding-right: 0;
        }
      }
    }

    &.resources-center-offer {
      .site-section {
        .video-container {
          .video-player {
            border: 12px solid var(--main-dark-light-2);
          }
        }
      }
    }

    &.upgrade-offer {
      .landing-hero-section {
        padding-top: var(--spacing-70);
      }

      .site-section {
        .bottom-banner {
          .container-wide {
            .heading {
              span {
                font-size: 24px;
              }
            }

            .counter {
              max-width: 308px;

              .square span:not(.display-text) {
                font-size: 28px;
              }
            }

            .btn-primary {
              width: 140px;
              padding: var(--spacing-20) 0;
            }
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .landing-page {
    padding-bottom: 0;

    .landing-hero-section {
      .hero-left {
        .heading h1 {
          font-size: 32px;
        }

        .subtitle {
          font-size: 16px;
        }
      }

      .col-5 {
        padding-left: 12px;
      }
    }

    .site-section {
      padding-top: 0;
      padding-bottom: 0;

      .industry-experts {
        .subtitle p {
          font-size: 16px;
        }
      }

      .program-stats {
        .row .btn-primary.btn-mobile {
          margin-top: var(--spacing-32);
        }
      }
    }

    &.resources-center-offer {
      .landing-hero-section {
        padding-top: var(--spacing-40);

        .hero-footer {
          margin-top: var(--spacing-100);

          .col-2 {
            height: 30px;
            margin-top: var(--spacing-8) !important;
          }
        }
      }

      .site-section {
        padding-top: 0;

        .limited-offer {
          .limited-offer-container {
            padding: 0 var(--spacing-24);

            h2 {
              width: 60%;
              margin-left: auto;
              margin-right: auto;
            }

            .plan-features {
              width: 60%;
            }
          }
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .landing-page {
    .background-image {
      &.mobile {
        display: block;
      }

      &.desktop {
        display: none;
      }
    }

    .landing-hero-section {
      padding-bottom: var(--spacing-40);
    }

    .components-wrapper {
      .reviews {
        order: 1;
      }
    }

    .site-section {
      .video-container,
      .image-container,
      .industry-experts,
      .program-cards,
      .data-science-cards,
      .reviews,
      .trusted,
      .limited-offer {
        padding-top: var(--spacing-60);
        padding-bottom: var(--spacing-60);

        .btn-primary {
          height: 63px;
          max-width: 100%;
        }
      }

      .image-container {
        img {
          max-width: unset;
        }
      }

      .video-container {
        .player.video-player.video-js {
          border-radius: 4px;
          border: 5px solid var(--main-dark-light-2);
        }
      }

      .limited-offer {
        .limited-offer-container .plan-features .feature {
          font-size: 12px;
        }
      }
    }

    &.resources-center-offer {
      .data-science-cards .cards .card {
        padding: var(--spacing-48) var(--spacing-28);

        svg {
          margin-bottom: 20px;
        }

        .h4 {
          font-size: 24px;
        }

        span {
          font-size: 16px;
          line-height: 140%;
        }
      }

      .site-section {
        .limited-offer {
          .limited-offer-container {
            max-width: 100%;

            h2,
            .plan-features {
              width: 100%;
            }

            .plan-features {
              column-gap: 8px;
              margin-bottom: var(--spacing-8);

              .feature {
                font-size: 12px;
                line-height: 18px;
                margin-bottom: 26px;
              }
            }
          }
        }

        .data-science-cards {
          .cards.row .col-4 .card {
            padding: var(--spacing-50) 27px;
          }
        }
      }
    }

    &.start-learning {
      .landing-hero-section {
        .card .discounted-price {
          font-size: 58px;

          @media screen and (max-width: 420px) {
            font-size: 13.2vw;

            .regular-price {
              font-size: 5vw;
            }
          }
        }
      }
    }

    &.start-learning,
    &.start-learning-m,
    &.start-learning-l {
      .landing-hero-section {
        padding-top: 52px;
      }

      .site-section {
        .program-stats .stats {
          .btn-primary {
            margin-top: var(--spacing-32);
          }
        }
      }
    }
  }
}

.fa {
  @import '../assets/sass/fa/pages/landing-page.scss';
}
</style>
